/**
 * catalog-cache.ts — opportunistic catalog caching to IndexedDB (S4-T3/T4/T5).
 *
 * Two thin wrappers:
 *   cacheCatalog    — fetch online, persist to IDB on success. Throws on fetch failure.
 *   getCachedCatalog — network-first with IDB fallback when offline or fetch fails.
 *
 * ponytail: two functions, zero new abstractions. The catalogs store (keyPath='key')
 * already exists from S1. Ceiling = fine for two small catalogs; upgrade = per-entity
 * stores if catalogs grow or need partial queries.
 */
import { getDb } from "../stores/db.js";
import type { CatalogEntry } from "../types.js";

type CatalogKey = CatalogEntry["key"]; // "clientes" | "marca"

/**
 * cacheCatalog — call fetchFn, write result to catalogs IDB store on success.
 *
 * Returns the fetched data. Throws if fetchFn throws (does NOT write on failure).
 */
export async function cacheCatalog<T>(
  key: CatalogKey,
  fetchFn: () => Promise<T>,
): Promise<T> {
  const data = await fetchFn(); // throws on network error — caller decides retry
  const db = await getDb();
  await db.put("catalogs", {
    key,
    data,
    cachedAt: new Date().toISOString(),
  } satisfies CatalogEntry);
  return data;
}

/**
 * putCachedCatalog — write a catalog entry to IDB directly, without an
 * accompanying fetch. Used when the caller already has the data in hand
 * (e.g. an enriched copy with data-URI logos) and just needs to persist it.
 */
export async function putCachedCatalog<T>(key: CatalogKey, data: T): Promise<void> {
  const db = await getDb();
  await db.put("catalogs", {
    key,
    data,
    cachedAt: new Date().toISOString(),
  } satisfies CatalogEntry);
}

/**
 * getCachedCatalogOnly — read straight from IDB, no network, no fallback.
 * Returns undefined when nothing is cached, so callers can distinguish "no
 * cache" from real data (unlike getCachedCatalog, whose empty-array fallback
 * only makes sense for list-shaped catalogs, not singleton objects like marca).
 */
export async function getCachedCatalogOnly<T>(key: CatalogKey): Promise<T | undefined> {
  const db = await getDb();
  const entry = (await db.get("catalogs", key)) as CatalogEntry | undefined;
  return entry ? (entry.data as T) : undefined;
}

/**
 * getCachedCatalog — network-first catalog read with IDB offline fallback.
 *
 * - If offline (isOnline() === false): skip network, return IDB cache or empty array.
 * - If online: fetch via fetchFn (which also caches via cacheCatalog).
 *   On fetch failure: fall back to IDB cache.
 *   If no cache exists: return empty array (safe default for selector population).
 *
 * isOnline defaults to () => navigator.onLine (injectable for tests).
 *
 * ponytail: network-first, IDB fallback. Ceiling = stale data shown if user goes
 * offline during a session; upgrade = cache-then-network with stale indicator.
 */
export async function getCachedCatalog<T>(
  key: CatalogKey,
  fetchFn: () => Promise<T>,
  isOnline: () => boolean = () => navigator.onLine,
): Promise<T> {
  // Offline: skip network entirely, go straight to cache.
  if (!isOnline()) {
    return readFromCache<T>(key);
  }

  // Online: try network first (fetchFn should also cache via cacheCatalog at the call site).
  try {
    return await fetchFn();
  } catch {
    // Network failed even though nominally online — fall back to IDB cache.
    return readFromCache<T>(key);
  }
}

async function readFromCache<T>(key: CatalogKey): Promise<T> {
  const db = await getDb();
  const entry = await db.get("catalogs", key) as CatalogEntry | undefined;
  if (entry) return entry.data as T;
  // No cache: return empty array as a safe default (selectors render empty, form still usable).
  return [] as unknown as T;
}
